import { useEffect, useState } from 'react';

import { Button, FieldLabel, Modal, showSnackbar, Table, TextInput } from 'fluxo-ui';
import {
    DownloadIcon,
    EyeIcon,
    FileAudioIcon,
    FileIcon,
    FilePdfIcon,
    FileTextIcon,
    FileVideoIcon,
    ImageIcon,
    PlusIcon,
    SearchIcon,
    TrashIcon,
} from 'fluxo-ui/icons';

import mediaService from '../services/media.service';

import type { ComponentEvent } from 'fluxo-ui';

interface Resource {
    _id?: string;
    mediaId?: string;
    fileName: string;
    fileType: string;
    mimeType?: string;
    fileSize: number;
    url: string;
    thumbnailUrl?: string;
    uploadedBy?: string;
    uploadedAt?: string;
    createdAt: string;
}

const ResourcesPage: React.FC = () => {
    const [resources, setResources] = useState<Resource[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [previewResource, setPreviewResource] = useState<Resource | null>(null);

    const fetchResources = async () => {
        setLoading(true);
        try {
            const { media } = await mediaService.getAllMedia({ search: searchQuery });
            setResources(
                media.map((m) => ({
                    _id: m.mediaId,
                    mediaId: m.mediaId,
                    fileName: m.fileName,
                    fileType: m.mimeType,
                    mimeType: m.mimeType,
                    fileSize: m.size,
                    url: m.url,
                    createdAt: m.uploadedAt,
                    uploadedAt: m.uploadedAt,
                })),
            );
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to load resources', 'Error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, []);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setFileName(file.name);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            showSnackbar('Please select a file', 'Error', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            await mediaService.uploadFile(selectedFile);
            showSnackbar('File uploaded successfully', 'Success', { type: 'success' });
            setShowUploadModal(false);
            setSelectedFile(null);
            setFileName('');
            fetchResources();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to upload file', 'Error', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (resourceId: string) => {
        if (!window.confirm('Are you sure you want to delete this resource?')) {
            return;
        }

        try {
            await mediaService.deleteFile(resourceId);
            showSnackbar('Resource deleted successfully', 'Success', { type: 'success' });
            fetchResources();
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to delete resource', 'Error', { type: 'error' });
        }
    };

    const handleDownload = (resource: Resource) => {
        const link = document.createElement('a');
        link.href = resource.url;
        link.download = resource.fileName;
        link.click();
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    // Presentational metadata (icon + colors) per file type
    const getFileTypeMeta = (mime: string = '') => {
        if (mime.startsWith('image/'))
            return {
                Icon: ImageIcon,
                label: 'IMAGE',
                chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
                tile: 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300',
            };
        if (mime.startsWith('video/'))
            return {
                Icon: FileVideoIcon,
                label: 'VIDEO',
                chip: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
                tile: 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300',
            };
        if (mime.startsWith('audio/'))
            return {
                Icon: FileAudioIcon,
                label: 'AUDIO',
                chip: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
                tile: 'bg-teal-100 text-teal-600 dark:bg-teal-900/40 dark:text-teal-300',
            };
        if (mime.includes('pdf'))
            return {
                Icon: FilePdfIcon,
                label: 'PDF',
                chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
                tile: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300',
            };
        if (mime.includes('word') || mime.includes('document') || mime.includes('presentation') || mime.startsWith('text/'))
            return {
                Icon: FileTextIcon,
                label: 'DOCUMENT',
                chip: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
                tile: 'bg-teal-100 text-teal-600 dark:bg-teal-900/40 dark:text-teal-300',
            };
        return {
            Icon: FileIcon,
            label: 'FILE',
            chip: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
            tile: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
        };
    };

    const filteredResources = resources.filter((resource) => resource.fileName.toLowerCase().includes(searchQuery.toLowerCase()));

    const columns = [
        {
            field: 'preview',
            title: 'Preview',
            render: (resource: Resource) => {
                const meta = getFileTypeMeta(resource.fileType || resource.mimeType);
                return (
                    <div className="flex items-center justify-center w-12 h-12">
                        {resource.fileType.startsWith('image/') ? (
                            <img
                                src={resource.thumbnailUrl || resource.url}
                                alt={resource.fileName}
                                className="w-full h-full object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                            />
                        ) : (
                            <div className={`w-full h-full rounded-lg flex items-center justify-center ${meta.tile}`}>
                                <meta.Icon className="w-6 h-6" />
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'fileName',
            title: 'File Name',
            render: (resource: Resource) => (
                <div>
                    <div className="font-medium text-slate-900 dark:text-white">{resource.fileName}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">ID: {resource._id || resource.mediaId}</div>
                </div>
            ),
        },
        {
            field: 'fileType',
            title: 'Type',
            render: (resource: Resource) => {
                const meta = getFileTypeMeta(resource.fileType || resource.mimeType);
                return (
                    <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full ${meta.chip}`}
                        title={resource.fileType || resource.mimeType}
                    >
                        <meta.Icon className="w-3.5 h-3.5" />
                        {meta.label}
                    </span>
                );
            },
        },
        {
            field: 'fileSize',
            title: 'Size',
            render: (resource: Resource) => formatFileSize(resource.fileSize),
        },
        {
            field: 'uploadedBy',
            title: 'Uploaded By',
        },
        {
            field: 'createdAt',
            title: 'Upload Date',
            render: (resource: Resource) => new Date(resource.createdAt).toLocaleDateString(),
        },
        {
            field: 'actions',
            title: 'Actions',
            render: (resource: Resource) => (
                <div className="flex items-center gap-2">
                    <Button leftIcon={EyeIcon} variant="default" size="sm" onClick={() => setPreviewResource(resource)} title="Preview" />
                    <Button leftIcon={DownloadIcon} variant="default" size="sm" onClick={() => handleDownload(resource)} title="Download" />
                    <Button
                        leftIcon={TrashIcon}
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(resource._id || resource.mediaId || '')}
                        title="Delete"
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Resource Library</h1>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Manage images, videos, documents, and other files that can be used in questions
                    </p>
                </div>

                {/* Toolbar */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex-1 max-w-md">
                        <TextInput
                            value={searchQuery}
                            onChange={(e: ComponentEvent<string>) => setSearchQuery(e.value)}
                            placeholder="Search resources..."
                            leftIcon={<SearchIcon />}
                        />
                    </div>
                    <Button label="Upload Resource" leftIcon={PlusIcon} variant="primary" onClick={() => setShowUploadModal(true)} />
                </div>

                {/* Resources Table */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                    <Table
                        rows={filteredResources}
                        totalRows={filteredResources.length}
                        columns={columns}
                        isLoading={loading}
                        noRowsMessage="No resources found. Upload your first resource to get started."
                    />
                </div>

                {/* Upload Modal */}
                <Modal
                    isOpen={showUploadModal}
                    onClose={() => !uploading && setShowUploadModal(false)}
                    title="Upload New Resource"
                    size="md"
                >
                    <div className="space-y-4">
                        <FieldLabel label="File Name" hint="Enter a descriptive name for this resource">
                            <TextInput
                                value={fileName}
                                onChange={(e: ComponentEvent<string>) => setFileName(e.value)}
                                placeholder="e.g., react_logo, python_tutorial_video"
                                disabled={uploading}
                            />
                        </FieldLabel>

                        <FieldLabel label="Select File">
                            <input
                                type="file"
                                onChange={handleFileSelect}
                                disabled={uploading}
                                className="block w-full text-sm text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer bg-slate-50 dark:bg-slate-700 focus:outline-none p-2"
                                accept="image/*,video/*,.pdf,.doc,.docx,.ppt,.pptx"
                            />
                        </FieldLabel>

                        {selectedFile && (
                            <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                                <p className="text-sm text-slate-700 dark:text-slate-300">
                                    <strong>Selected:</strong> {selectedFile.name}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Size: {formatFileSize(selectedFile.size)}</p>
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-3 pt-4">
                            <Button
                                label="Cancel"
                                variant="default"
                                layout="outlined"
                                onClick={() => setShowUploadModal(false)}
                                disabled={uploading}
                            />
                            <Button
                                label="Upload"
                                variant="primary"
                                onClick={handleUpload}
                                isLoading={uploading}
                                disabled={!selectedFile || uploading}
                            />
                        </div>
                    </div>
                </Modal>

                {/* Preview Modal */}
                {previewResource && (
                    <Modal isOpen={!!previewResource} onClose={() => setPreviewResource(null)} title={previewResource.fileName} size="lg">
                        <div className="space-y-4">
                            {(previewResource.fileType?.startsWith('image/') || previewResource.mimeType?.startsWith('image/')) && (
                                <img src={previewResource.url} alt={previewResource.fileName} className="w-full h-auto rounded-lg" />
                            )}
                            {(previewResource.fileType?.startsWith('video/') || previewResource.mimeType?.startsWith('video/')) && (
                                <video src={previewResource.url} controls className="w-full rounded-lg" />
                            )}
                            <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Resource ID:</span>
                                    <code className="bg-white dark:bg-slate-800 px-2 py-0.5 rounded">
                                        {previewResource._id || previewResource.mediaId}
                                    </code>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Usage in question:</span>
                                    <code className="bg-white dark:bg-slate-800 px-2 py-0.5 rounded">
                                        {'{'}image:{previewResource._id || previewResource.mediaId}
                                        {'}'}
                                    </code>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">File Type:</span>
                                    <span>{previewResource.fileType || previewResource.mimeType}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600 dark:text-slate-400">Size:</span>
                                    <span>{formatFileSize(previewResource.fileSize)}</span>
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        </div>
    );
};

export default ResourcesPage;
